import React, { useState } from 'react';
import { format } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { View, CheckCircle2, Calendar, DollarSign, Clock, AlertTriangle, ChevronDown, ChevronUp, Package } from 'lucide-react';
import { usePaymentConfirmationProcurements } from '@/hooks/use-payment-confirmation-procurements';
import { PaymentConfirmationDialog } from './PaymentConfirmationDialog';
import { AdvancePaymentConfirmationDialog } from './AdvancePaymentConfirmationDialog';
import { useQueryClient } from '@tanstack/react-query';
import { CustomPagination } from '@/components/ui/custom-pagination';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

const FinancePaymentConfirmationList: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const { data: result, isLoading } = usePaymentConfirmationProcurements({ 
    page: currentPage, 
    pageSize 
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedProcurement, setSelectedProcurement] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false);
  const [paymentType, setPaymentType] = useState<'advance' | 'remaining'>('advance');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleItemExpansion = (procurementId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(procurementId)) {
        newSet.delete(procurementId);
      } else {
        newSet.add(procurementId);
      }
      return newSet;
    });
  };

  // Type guard to check if result has pagination structure
  const isPaginatedResult = (data: any): data is { data: any[], totalPages: number } => {
    return data && typeof data === 'object' && 'data' in data && 'totalPages' in data;
  };

  const procurements = isPaginatedResult(result) ? result.data : [];
  const totalPages = isPaginatedResult(result) ? result.totalPages : 0;

  // Filter procurements that are approved but payment not confirmed
  const awaitingPaymentConfirmation = procurements.filter(p => {
    if (!p.finance) return false;
    
    const hasAdvancePayment = p.finance.has_advance_payment;
    const advanceConfirmed = p.finance.advance_payment_confirmed;
    const remainingConfirmed = p.finance.remaining_payment_confirmed;
    const fullPaymentConfirmed = p.finance.payment_confirmed;
    
    // Show if: has advance payment and either advance or remaining is not confirmed
    // OR: no advance payment and full payment is not confirmed
    return hasAdvancePayment 
      ? (!advanceConfirmed || !remainingConfirmed)
      : !fullPaymentConfirmed;
  });

  const handleConfirmAdvancePayment = (procurement: any) => {
    setSelectedProcurement(procurement);
    setPaymentType('advance');
    setAdvanceDialogOpen(true);
  };

  const handleConfirmRemainingPayment = (procurement: any) => {
    setSelectedProcurement(procurement);
    setPaymentType('remaining');
    setAdvanceDialogOpen(true);
  };

  const handleConfirmFullPayment = (procurement: any) => {
    setSelectedProcurement(procurement);
    setDialogOpen(true);
  };

  const handlePaymentConfirmed = () => {
    queryClient.invalidateQueries({ queryKey: ['payment-confirmation-procurements'] });
    queryClient.invalidateQueries({ queryKey: ['procurements'] });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  if (awaitingPaymentConfirmation.length === 0) {
    return (
      <div className="text-center py-8">
        <CheckCircle2 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-muted-foreground">No payments awaiting confirmation.</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {awaitingPaymentConfirmation.map((procurement) => {
          const hasAdvancePayment = procurement.finance?.has_advance_payment;
          const advanceConfirmed = procurement.finance?.advance_payment_confirmed;
          const remainingConfirmed = procurement.finance?.remaining_payment_confirmed;
          const fullPaymentConfirmed = procurement.finance?.payment_confirmed;

          // Quality grading info
          const quality = procurement.quality;
          const isPreliminary = quality?.grading_type === 'preliminary';
          const isFinalComplete = quality?.is_final_grading_complete;
          const hasBothGrades = isPreliminary && isFinalComplete;
          const prelimComp = quality?.preliminary_compensation_kg || 0;
          const finalComp = quality?.compensation_kg || 0;
          const compensationDiff = hasBothGrades ? finalComp - prelimComp : 0;

          return (
            <Card key={procurement.id}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium">{procurement.product_name || 'N/A'}</h3>
                      {hasAdvancePayment ? (
                        <div className="flex gap-1">
                          <Badge variant={advanceConfirmed ? "default" : "secondary"}>
                            Advance: {advanceConfirmed ? 'Confirmed' : 'Pending'}
                          </Badge>
                          <Badge variant={remainingConfirmed ? "default" : "secondary"}>
                            Remaining: {remainingConfirmed ? 'Confirmed' : 'Pending'}
                          </Badge>
                        </div>
                      ) : (
                        <Badge variant="secondary">Awaiting Payment Confirmation</Badge>
                      )}
                      {/* Grading Type Badge */}
                      {isPreliminary && (
                        isFinalComplete ? (
                          <Badge className="bg-green-600">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Final Graded
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                            <Clock className="h-3 w-3 mr-1" />
                            Preliminary
                          </Badge>
                        )
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4" />
                        <span className="font-medium">Total Amount:</span> Br{procurement.total_amount?.toFixed(2) || '0.00'}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="font-medium">Quantity:</span> {procurement.total_quantity || 0} kg
                      </div>
                      
                      {hasAdvancePayment && (
                        <>
                          <div>
                            <span className="font-medium">Advance:</span> Br{procurement.finance?.advance_payment_amount?.toFixed(2) || '0.00'}
                          </div>
                          <div>
                            <span className="font-medium">Remaining:</span> Br{procurement.finance?.remaining_payment_amount?.toFixed(2) || '0.00'}
                          </div>
                        </>
                      )}
                      
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span className="font-medium">Due Date:</span> {
                          procurement.finance?.payment_due_date 
                            ? format(new Date(procurement.finance.payment_due_date), 'PP')
                            : 'N/A'
                        }
                      </div>
                      <div>
                        <span className="font-medium">Payment Type:</span> {
                          procurement.finance?.payment_term_type === 'upfront' ? 'Upfront' : 'Term'
                        }
                      </div>
                    </div>

                    {/* Quality Inspection Findings - Preliminary vs Final */}
                    {quality && (isPreliminary || quality.damage_kg > 0 || quality.compensation_kg > 0) && (
                      <div className="space-y-2">
                        {/* Preliminary Assessment */}
                        {isPreliminary && (
                          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
                            <p className="font-medium text-amber-800 mb-1 flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              Preliminary Assessment (Sample-based)
                            </p>
                            <div className="grid grid-cols-3 gap-2 text-amber-700">
                              <div>
                                <span className="font-medium">Est. Damage:</span> {quality.preliminary_damage_kg || 0} kg
                              </div>
                              <div>
                                <span className="font-medium">Est. Compensation:</span> {prelimComp} kg
                              </div>
                              <div>
                                <span className="font-medium">Net Payable (Prelim):</span> {(procurement.total_quantity || 0) - prelimComp} kg
                              </div>
                            </div>
                            {!isFinalComplete && quality.final_grading_expected_date && (
                              <p className="text-xs text-amber-600 mt-1">
                                Expected Final Grading: {format(new Date(quality.final_grading_expected_date), 'PP')}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Final Assessment */}
                        {isFinalComplete && (
                          <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm">
                            <p className="font-medium text-green-800 mb-1 flex items-center gap-1">
                              <CheckCircle2 className="h-4 w-4" />
                              Final Assessment (Complete)
                            </p>
                            <div className="grid grid-cols-3 gap-2 text-green-700">
                              <div>
                                <span className="font-medium">Actual Damage:</span> {quality.damage_kg || 0} kg
                              </div>
                              <div>
                                <span className="font-medium">Actual Compensation:</span> {finalComp} kg
                              </div>
                              <div>
                                <span className="font-medium">Net Payable (Final):</span> {(procurement.total_quantity || 0) - finalComp} kg
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Reconciliation Alert */}
                        {hasBothGrades && compensationDiff !== 0 && (
                          <div className={`rounded-md p-3 text-sm ${compensationDiff > 0 ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                            <p className={`font-medium mb-1 flex items-center gap-1 ${compensationDiff > 0 ? 'text-red-800' : 'text-green-800'}`}>
                              <AlertTriangle className="h-4 w-4" />
                              Reconciliation Required
                            </p>
                            <div className={compensationDiff > 0 ? 'text-red-700' : 'text-green-700'}>
                              <span className="font-medium">Difference:</span> {Math.abs(compensationDiff)} kg
                              <span className="ml-2">
                                {compensationDiff > 0 
                                  ? '(Final damage MORE than estimated)'
                                  : '(Final damage LESS than estimated)'
                                }
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Non-preliminary quality findings */}
                        {!isPreliminary && (quality.damage_kg > 0 || quality.compensation_kg > 0) && (
                          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
                            <p className="font-medium text-amber-800 mb-1">Quality Inspection Findings</p>
                            <div className="grid grid-cols-3 gap-2 text-amber-700">
                              <div>
                                <span className="font-medium">Damage:</span> {quality.damage_kg || 0} kg
                              </div>
                              <div>
                                <span className="font-medium">Compensation:</span> {quality.compensation_kg || 0} kg
                              </div>
                              <div>
                                <span className="font-medium">Net Payable:</span> {(procurement.total_quantity || 0) - (quality.compensation_kg || 0)} kg
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="text-sm">
                      <span className="font-medium">Source:</span> {procurement.source_name || 'N/A'} ({procurement.source_type})
                    </div>

                    {/* Item-level breakdown for multi-product procurements */}
                    {procurement.items && procurement.items.length > 1 && (
                      <Collapsible 
                        open={expandedItems.has(procurement.id)} 
                        onOpenChange={() => toggleItemExpansion(procurement.id)}
                      >
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="w-full justify-between mt-2">
                            <span className="flex items-center gap-2">
                              <Package className="h-4 w-4" />
                              {procurement.items.length} Products
                            </span>
                            {expandedItems.has(procurement.id) ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-2">
                          <div className="bg-muted/50 rounded-md p-3 space-y-2">
                            {procurement.items.map((item: any) => {
                              const itemQuality = item.item_quality?.[0];
                              return (
                                <div key={item.id} className="flex justify-between items-center text-sm border-b border-border/50 pb-2 last:border-0 last:pb-0">
                                  <div className="flex-1">
                                    <span className="font-medium">{item.product_name}</span>
                                    <span className="text-muted-foreground ml-2">({item.quantity} kg)</span>
                                  </div>
                                  <div className="flex gap-4 text-muted-foreground">
                                    {itemQuality?.quality_grade && (
                                      <Badge variant="outline" className="text-xs">
                                        Grade: {itemQuality.quality_grade}
                                      </Badge>
                                    )}
                                    {(itemQuality?.compensation_kg > 0 || itemQuality?.preliminary_compensation_kg > 0) && (
                                      <span className="text-amber-600">
                                        Comp: {itemQuality?.compensation_kg || itemQuality?.preliminary_compensation_kg || 0} kg
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </div>
                  
                  <div className="ml-4 flex flex-col gap-2">
                    {hasAdvancePayment ? (
                      <>
                        {!advanceConfirmed && (
                          <Button
                            onClick={() => handleConfirmAdvancePayment(procurement)}
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <CheckCircle2 className="mr-1 h-4 w-4" />
                            Confirm Advance
                          </Button>
                        )}
                        {!remainingConfirmed && advanceConfirmed && (
                          <Button
                            onClick={() => handleConfirmRemainingPayment(procurement)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle2 className="mr-1 h-4 w-4" />
                            Confirm Remaining
                          </Button>
                        )}
                      </>
                    ) : (
                      !fullPaymentConfirmed && (
                        <Button
                          onClick={() => handleConfirmFullPayment(procurement)}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle2 className="mr-1 h-4 w-4" />
                          Mark as Paid
                        </Button>
                      )
                    )}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/procurements/${procurement.id}`)}
                    >
                      <View className="mr-1 h-4 w-4" />
                      View Details
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {totalPages > 1 && (
        <CustomPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          className="mt-6"
        />
      )}

      {selectedProcurement && (
        <>
          <PaymentConfirmationDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            procurement={selectedProcurement}
            onConfirm={handlePaymentConfirmed}
          />
          
          <AdvancePaymentConfirmationDialog
            open={advanceDialogOpen}
            onOpenChange={setAdvanceDialogOpen}
            procurement={selectedProcurement}
            onConfirm={handlePaymentConfirmed}
            paymentType={paymentType}
          />
        </>
      )}
    </>
  );
};

export default FinancePaymentConfirmationList;
